import React, { useRef, useState } from 'react';
import PageForm, { FormField, FieldType } from './PageForm';

// ── API field → FormField mapper ──────────────────────────────────────────────

const TYPE_MAP: Record<string, FieldType> = {
  text:         'text',
  textarea:     'textarea',
  number:       'number',
  email:        'email',
  date:         'date',
  checkbox:     'checkbox',
  select:       'select',
  combox:       'select',    // backend uses "combox"
  combobox:     'select',
  autocomplete: 'autocomplete-on-demand',
  file:         'file',
};

export interface GenericFormAction {
  label: string;
  type: 'submit' | 'cancel' | string;
  param?: string | null;
  action?: string | null;
}

export interface GenericFormDef {
  fields: {
    name: string;
    label: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
    options?: { label: string; value: string }[] | null;
    url?: string | null;
    queryParam?: string | null;
    valueKey?: string | null;
    labelKey?: string | null;
    minChars?: number | null;
    extraParams?: Record<string, string> | null;
    readOnly?: boolean;
  }[];
  actions?: GenericFormAction[];
}

export interface GenericFormProps {
  /** Parsed form definition from the API */
  formDef: GenericFormDef;
  /** Pre-populated values fetched from the server */
  initialValues?: Record<string, any>;
  /** Called when the user submits — receives the current values map */
  onSubmit: (values: Record<string, any>, action: GenericFormAction | null) => void;
  /** Called when the user cancels */
  onCancel: () => void;
  saving?: boolean;
  accentColor?: string;
}

export default function GenericForm({
  formDef,
  initialValues = {},
  onSubmit,
  onCancel,
  saving = false,
  accentColor = '#6366f1',
}: GenericFormProps) {
  // Merge server values over empty defaults so every field name is present
  const [values, setValues] = useState<Record<string, any>>(() => ({
    ...Object.fromEntries(formDef.fields.map(f => [f.name, ''])),
    ...initialValues,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Collect raw File objects for file-type fields (keyed by field name)
  const filesRef = useRef<Map<string, File | null>>(new Map());

  // Map API field definitions → PageForm FormField array
  const fields: FormField[] = formDef.fields.map(f => ({
    name:        f.name,
    label:       f.label,
    type:        (TYPE_MAP[f.type ?? 'text'] ?? 'text') as FieldType,
    required:    f.required ?? false,
    placeholder: f.placeholder ?? undefined,
    readOnly:    f.readOnly ?? false,
    options:     f.options?.map(o => ({ label: o.label, value: o.value })) ?? undefined,
    url:         f.url ?? undefined,
    queryParam:  f.queryParam ?? undefined,
    valueKey:    f.valueKey ?? undefined,
    labelKey:    f.labelKey ?? undefined,
    minChars:    f.minChars ?? undefined,
    extraParams: f.extraParams ?? undefined,
    // For file fields, capture the raw File object
    onFileSelect: (TYPE_MAP[f.type ?? ''] === 'file')
      ? (file: File | null) => { filesRef.current.set(f.name, file); }
      : undefined,
  }));

  const submitAction = formDef.actions?.find(a => a.type === 'submit') ?? null;
  const cancelAction = formDef.actions?.find(a => a.type === 'cancel') ?? null;

  const handleChange = (name: keyof Record<string, any>, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
    if (errors[name as string]) setErrors(prev => ({ ...prev, [name as string]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic required validation
    const newErrors: Record<string, string> = {};
    fields.forEach(f => {
      if (f.required && !values[f.name as string]) {
        newErrors[f.name as string] = `${f.label} es requerido`;
      }
    });
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    // Attach collected File objects to values so the caller can build FormData
    const fileEntries = Array.from(filesRef.current.entries());
    const hasFiles = fileEntries.some(([, v]) => v instanceof File);
    const enriched: Record<string, any> = { ...values };
    if (hasFiles) {
      enriched._files = Object.fromEntries(
        fileEntries.filter(([, v]) => v instanceof File)
      );
    }
    onSubmit(enriched, submitAction);
  };

  const handleCancel = () => {
    onCancel();
    if (cancelAction?.action) window.location.hash = cancelAction.action;
  };

  return (
    <PageForm
      fields={fields}
      values={values}
      errors={errors}
      isNew
      saving={saving}
      onFieldChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      accentColor={accentColor}
      submitLabel={submitAction?.label ?? 'Guardar'}
      cancelLabel={cancelAction?.label ?? 'Cancelar'}
    />
  );
}
